/**
 * El tema, con tres estados y no dos.
 *
 * La persona puede haber elegido «clásico», haber elegido «oscuro», o no
 * haber elegido nada. Ese tercer estado es el que importa: mientras no
 * elija, mandamos lo que el sistema pida (prefers-color-scheme) y la
 * pantalla acompaña al resto del teléfono. En cuanto elige, manda su
 * elección en los dos sentidos, aunque el sistema diga lo contrario.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_THEME, THEMES, type Theme } from '@tennisup/contracts';

import { readStored, writeStored } from '../lib/storage';

export const THEME_STORAGE_KEY = 'tennisup.theme';

interface ThemeContextValue {
  /** El tema que se está viendo ahora mismo, ya resuelto. */
  theme: Theme;
  /** Lo que la persona eligió; null si nunca eligió y manda el sistema. */
  preference: Theme | null;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isTheme(value: string | null): value is Theme {
  return value !== null && (THEMES as readonly string[]).includes(value);
}

function systemTheme(): Theme {
  // matchMedia no existe en jsdom ni en navegadores muy viejos; sin él,
  // el default del producto.
  if (typeof window.matchMedia !== 'function') return DEFAULT_THEME;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'DARK' : DEFAULT_THEME;
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const stored = readStored(THEME_STORAGE_KEY);
  const [preference, setPreference] = useState<Theme | null>(isTheme(stored) ? stored : null);
  const [system, setSystem] = useState<Theme>(systemTheme);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent): void => setSystem(event.matches ? 'DARK' : DEFAULT_THEME);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const theme = preference ?? system;

  useEffect(() => {
    // Sin elección explícita no ponemos el atributo: así la media query del
    // CSS sigue mandando y el tema acompaña al sistema si cambia.
    if (preference === null) {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', preference);
    }
  }, [preference]);

  const setTheme = useCallback((next: Theme) => {
    setPreference(next);
    writeStored(THEME_STORAGE_KEY, next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, preference, setTheme }),
    [theme, preference, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) throw new Error('useTheme fuera de <ThemeProvider>');
  return value;
}
